import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, Package, Rocket, Upload } from 'lucide-react';
import FirmwareUpload from './FirmwareUpload';
import FirmwareList from './FirmwareList';
import UpgradeMonitor from './UpgradeMonitor';

const TAB_IDS = new Set(['deploy', 'upload', 'monitor']);

export default function FirmwareUpgrade({ defaultTab = 'deploy' }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(TAB_IDS.has(defaultTab) ? defaultTab : 'deploy');

  useEffect(() => {
    setActiveTab(TAB_IDS.has(defaultTab) ? defaultTab : 'deploy');
  }, [defaultTab]);

  const tabs = useMemo(() => [
    {
      id: 'deploy',
      label: t('firmware.upgrade.deployTab', { defaultValue: 'Firmware Upgrade' }),
      hint: t('firmware.upgrade.deployHint', { defaultValue: 'Select a version and device' }),
      icon: Rocket,
    },
    {
      id: 'upload',
      label: t('firmware.upload.title', { defaultValue: 'Upload Firmware' }),
      hint: t('firmware.upgrade.uploadHint', { defaultValue: 'Add a firmware version' }),
      icon: Upload,
    },
    {
      id: 'monitor',
      label: t('firmware.monitor.title', { defaultValue: 'Upgrade Monitor' }),
      hint: t('firmware.upgrade.monitorHint', { defaultValue: 'View upgrade progress' }),
      icon: Activity,
    },
  ], [t]);

  const ActivePanel = activeTab === 'upload'
    ? FirmwareUpload
    : activeTab === 'monitor'
      ? UpgradeMonitor
      : FirmwareList;

  return (
    <div className="space-y-5 p-4 admin-text-primary sm:p-6 lg:p-8">
      <header className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-cyan-100 text-cyan-800">
            <Package size={26} />
          </span>
          <div className="min-w-0">
            <h2 className="break-words text-2xl font-black sm:text-3xl">
              {t('firmware.upgrade.title', { defaultValue: 'Firmware Upgrade' })}
            </h2>
            <p className="mt-1 max-w-4xl text-sm leading-6 admin-text-muted">
              {t('firmware.upgrade.subtitle', {
                defaultValue: 'Upload firmware, select versions and devices, and monitor upgrade progress on one page.',
              })}
            </p>
          </div>
        </div>
      </header>

      <nav
        className="admin-card grid gap-2 rounded-lg p-2 md:grid-cols-3"
        aria-label={t('firmware.upgrade.tabs', { defaultValue: 'Firmware upgrade sections' })}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex min-h-[72px] items-center gap-3 rounded-lg border px-4 py-3 text-left transition ${
                active
                  ? 'border-cyan-300 bg-cyan-50 text-cyan-900 shadow-sm'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-cyan-200 hover:bg-cyan-50/60'
              }`}
            >
              <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                active ? 'bg-cyan-500 text-white' : 'bg-slate-100 text-slate-600'
              }`}>
                <Icon size={20} />
              </span>
              <span className="min-w-0">
                <span className="block break-words text-sm font-black">{tab.label}</span>
                <span className="mt-1 block break-words text-xs opacity-80">{tab.hint}</span>
              </span>
            </button>
          );
        })}
      </nav>

      <section className="min-w-0">
        <ActivePanel />
      </section>
    </div>
  );
}
